import type {
  LandingSite,
  LandingSitePage,
  LandingSection,
  TenantBranding,
} from '../models';

const DEFAULT_PRIMARY = '#0D6BB1';
const DEFAULT_SECONDARY = '#198F79';

/** The BE falls back to this file when the tenant has no logo. */
export function isDefaultTenantLogo(url: string | null | undefined): boolean {
  return !!url && url.includes('default_logo');
}

/** Normalize BE `branding` (TenantBranding) with the documented fallbacks. */
export function fromBackendBranding(raw: any): TenantBranding {
  const b = raw && typeof raw === 'object' ? raw : {};
  const primaryColor = b.primaryColor || DEFAULT_PRIMARY;
  const secondaryColor = b.secondaryColor || DEFAULT_SECONDARY;
  const logo = b.logo || null;
  const favicon = b.favicon || null;
  return {
    logo,
    logoDarkMode: b.logoDarkMode || logo,
    favicon,
    faviconDarkMode: b.faviconDarkMode || favicon,
    primaryColor,
    secondaryColor,
    primaryColorDarkMode: b.primaryColorDarkMode || primaryColor,
    secondaryColorDarkMode: b.secondaryColorDarkMode || secondaryColor,
    lightBackground: b.lightBackground || null,
    darkBackground: b.darkBackground || null,
    fontFamily: b.fontFamily || null,
    borderRadius: typeof b.borderRadius === 'number' ? b.borderRadius : null,
    navAlign: ['start', 'center', 'end'].includes(b.navAlign)
      ? b.navAlign
      : null,
    themeMode: ['light', 'dark', 'both'].includes(b.themeMode)
      ? b.themeMode
      : 'light',
    allowDarkMode: b.allowDarkMode ?? true,
    extractedColors: Array.isArray(b.extractedColors) ? b.extractedColors : [],
  };
}

export function toUpdateWebsiteDto(site: Partial<LandingSite>): any {
  const dto: any = {};

  if (site.organizationName !== undefined) {
    dto.name = site.organizationName;
  }
  if (site.subdomain !== undefined) {
    dto.subdomain = site.subdomain;
  }

  if (site.metaTitle !== undefined || site.metaTitleAr !== undefined) {
    dto.metaTitle = {
      en: site.metaTitle || '',
      ar: site.metaTitleAr || '',
    };
  }

  if (
    site.metaDescription !== undefined ||
    site.metaDescriptionAr !== undefined
  ) {
    dto.metaDescription = {
      en: site.metaDescription || '',
      ar: site.metaDescriptionAr || '',
    };
  }

  if (site.langPreference !== undefined) {
    dto.languageMode = site.langPreference.toUpperCase();
  }

  if (site.about !== undefined || site.aboutAr !== undefined) {
    dto.about = site.about || '';
    dto.aboutAr = site.aboutAr || '';
  }

  // Branding is edited in tenant settings (PATCH /tenants/settings), never here
  const directFields = [
    'location',
    'mapLocation',
    'navItems',
    'socialLinks',
    'published',
  ] as const;

  directFields.forEach((field) => {
    if (site[field] !== undefined) {
      dto[field] = site[field];
    }
  });

  if (site.footerConfig !== undefined) {
    dto.footer = site.footerConfig;
  }

  return dto;
}

export function fromBackendWebsite(backend: any): LandingSite {
  return {
    id: String(backend.id), // Backend uses number, frontend uses string
    tenantId: String(backend.tenantId || backend.tenant_id),
    organizationName: backend.name || backend.organizationName || '',
    subdomain:
      backend.subdomain || backend.name || backend.organizationName || '',
    email: backend.email || '',
    phone: backend.phone || '',
    about: backend.about || '',
    aboutAr: backend.aboutAr || backend.about_ar || '',
    location: backend.location || '',
    mapLocation: backend.mapLocation || backend.map_location || null,
    langPreference: (backend.languageMode?.toLowerCase() ||
      backend.language_mode?.toLowerCase() ||
      'ar') as 'ar' | 'en',
    publicLangPref: backend.publicLangPref ||
      backend.public_lang_pref || ['ar'],
    inputLang: backend.inputLang || backend.input_lang || ['ar'],
    branding: fromBackendBranding(backend.branding),
    metaTitle: pickLocalized(backend.metaTitle || backend.meta_title, 'en'),
    metaTitleAr: pickLocalized(backend.metaTitle || backend.meta_title, 'ar'),
    metaDescription: pickLocalized(
      backend.metaDescription || backend.meta_description,
      'en',
    ),
    metaDescriptionAr: pickLocalized(
      backend.metaDescription || backend.meta_description,
      'ar',
    ),
    navItems: normalizeNavItems(backend.navItems || backend.nav_items || []),
    footerConfig: normalizeFooterConfig(
      backend.footer || backend.footer_config || backend.footerConfig || {},
    ),
    socialLinks: normalizeSocialLinks(
      backend.socialLinks || backend.social_links || [],
    ),
    published: backend.published ?? false,
    publishedAt: backend.publishedAt || backend.published_at || null,
    hasUnpublishedChanges:
      backend.hasUnpublishedChanges ?? backend.has_unpublished_changes ?? false,
    pages: (backend.pages || []).map(fromBackendPage),
    createdAt: backend.createdAt || backend.created_at,
    updatedAt: backend.updatedAt || backend.updated_at,
  };
}

/** Keep pageId/sectionId as strings so selects + public resolve match. */
function normalizeNavItems(raw: unknown): LandingSite['navItems'] {
  if (!Array.isArray(raw)) return [];
  const items: LandingSite['navItems'] = [];
  raw.forEach((item, index) => {
    if (!item || typeof item !== 'object') return;
    const n = item as Record<string, unknown>;
    const type = asString(n['type']) as LandingSite['navItems'][number]['type'];
    if (!type) return;

    const nav: LandingSite['navItems'][number] = {
      id: asString(n['id']) || `nav-${index}`,
      type,
      label: asString(n['label']),
      labelAr: asString(n['labelAr']) || asString(n['label_ar']),
    };
    if (n['pageId'] != null || n['page_id'] != null) {
      nav.pageId = String(n['pageId'] ?? n['page_id']);
    }
    if (n['sectionId'] != null || n['section_id'] != null) {
      nav.sectionId = String(n['sectionId'] ?? n['section_id']);
    }
    const url = asString(n['url']);
    if (url) nav.url = url;
    if (n['newTab'] ?? n['new_tab']) nav.newTab = true;
    items.push(nav);
  });
  return items;
}

/** Normalize BE footer jsonb (flat FE shape or LocalizedText / legacy keys). */
function normalizeFooterConfig(raw: unknown): LandingSite['footerConfig'] {
  if (!raw || typeof raw !== 'object') return {};
  const f = raw as Record<string, unknown>;

  // Prefer explicit flat EN/AR fields. Only treat `tagline`/`copyright` as
  // LocalizedText when they are objects — never use a flat EN string as AR
  // (pickLocalized returns strings as-is for any lang, which wiped Arabic).
  const taglineEn =
    asString(f['tagline']) || pickLocalizedObject(f['tagline'], 'en');
  const taglineAr =
    asString(f['taglineAr']) ||
    asString(f['tagline_ar']) ||
    pickLocalizedObject(f['tagline'], 'ar');

  const copyrightSource =
    f['copyright'] ?? f['copyrightText'] ?? f['copyright_text'];
  const copyrightEn =
    asString(f['copyright']) || pickLocalizedObject(copyrightSource, 'en');
  const copyrightAr =
    asString(f['copyrightAr']) ||
    asString(f['copyright_ar']) ||
    pickLocalizedObject(copyrightSource, 'ar');

  const linksRaw = Array.isArray(f['links']) ? f['links'] : [];
  const links = linksRaw
    .map((item, index) => {
      if (!item || typeof item !== 'object') return null;
      const link = item as Record<string, unknown>;
      const url = asString(link['url']);
      if (!url) return null;
      return {
        id: asString(link['id']) || `footer-link-${index}`,
        label:
          asString(link['label']) ||
          asString(link['labelEn']) ||
          pickLocalizedObject(link['label'], 'en'),
        labelAr:
          asString(link['labelAr']) ||
          asString(link['label_ar']) ||
          pickLocalizedObject(link['label'], 'ar'),
        url,
      };
    })
    .filter((l): l is NonNullable<typeof l> => !!l);

  return {
    showLogo: f['showLogo'] !== false && f['show_logo'] !== false,
    tagline: taglineEn,
    taglineAr,
    copyright: copyrightEn,
    copyrightAr,
    links,
  };
}

/** Like pickLocalized, but only for LocalizedText objects — never flat strings. */
function pickLocalizedObject(field: unknown, lang: 'en' | 'ar'): string {
  if (!field || typeof field !== 'object') return '';
  return pickLocalized(field, lang);
}

function normalizeSocialLinks(raw: unknown): LandingSite['socialLinks'] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item, index) => {
      if (!item || typeof item !== 'object') return null;
      const link = item as Record<string, unknown>;
      const url = asString(link['url']);
      const platform = asString(link['platform']);
      if (!url || !platform) return null;
      return {
        id: asString(link['id']) || `social-${platform}-${index}`,
        platform,
        url,
      };
    })
    .filter((l): l is NonNullable<typeof l> => !!l);
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/** Flatten LocalizedText (and recover double-wrapped objects) to a plain string. */
function pickLocalized(field: unknown, lang: 'en' | 'ar'): string {
  if (field == null) return '';
  if (typeof field === 'string') return field;
  if (typeof field !== 'object') return '';

  const obj = field as Record<string, unknown>;
  const direct = obj[lang];
  if (typeof direct === 'string') return direct;

  // Recover FE double-wrap: { en: { en: '', ar: '' }, ar: '' }
  if (direct && typeof direct === 'object') {
    const nested = direct as Record<string, unknown>;
    if (typeof nested[lang] === 'string') return nested[lang] as string;
    if (typeof nested['en'] === 'string') return nested['en'] as string;
    if (typeof nested['ar'] === 'string') return nested['ar'] as string;
  }

  return '';
}

export function fromBackendPage(backend: any): LandingSitePage {
  return {
    id: String(backend.id),
    websiteId: String(
      backend.websiteId || backend.website_id || backend.siteId,
    ),
    title:
      pickLocalized(backend.title, 'en') ||
      (typeof backend.title === 'string' ? backend.title : ''),
    titleAr:
      pickLocalized(backend.title, 'ar') ||
      (typeof backend.titleAr === 'string' ? backend.titleAr : ''),
    slug: backend.slug || '',
    order: backend.order ?? 0,
    showInNav: backend.showInNav ?? backend.show_in_nav ?? true,
    isHome: backend.isHome ?? backend.is_home ?? false,
    sections: (backend.sections || []).map(fromBackendSection),
    createdAt: backend.createdAt || backend.created_at,
    updatedAt: backend.updatedAt || backend.updated_at,
  };
}

export function fromBackendSection(backend: any): LandingSection {
  return {
    id: String(backend.id),
    pageId: String(backend.pageId || backend.page_id),
    type: backend.type?.toLowerCase() || backend.type,
    title: pickLocalized(backend.title, 'en'),
    titleAr:
      pickLocalized(backend.title, 'ar') ||
      (typeof backend.titleAr === 'string' ? backend.titleAr : ''),
    subtitle: pickLocalized(backend.subtitle, 'en'),
    subtitleAr:
      pickLocalized(backend.subtitle, 'ar') ||
      (typeof backend.subtitleAr === 'string' ? backend.subtitleAr : ''),
    anchor: backend.anchor || null,
    showInNav: backend.showInNav ?? backend.show_in_nav ?? false,
    order: backend.order ?? 0,
    isActive: true, // Backend doesn't have this, always true
    content: backend.content || {},
    settings: backend.settings || {},
    createdAt: backend.createdAt || backend.created_at,
    updatedAt: backend.updatedAt || backend.updated_at,
  };
}
